import { useState, useEffect, ReactEventHandler } from "react";
import Footer from "../components/footer";
import Menu from "../components/menu";
import ImgCompte from "../components/imgCompte";
import api from "../config/axios";
import PremiumSection from "../components/premiumSection";
import SubscriptionDetails from "../components/subscriptiondetails";

interface User {
    userId: number;
    prenom: string;
    nom: string;
    email: string;
    civiliteId: number;
    handId: number;
    classement: number;
    departementId: number;
    niveauId: number;
    canBeSearched: boolean;
    dispoTournoi: boolean;
    dispoLibre: boolean;
    isPremium: boolean;
}

interface THand {
    handId: number;
    handLib: string;
}

interface TDepartement {
    departementId: number;
    departementNum: string;
    departementLib: string;
}

interface TNiveau {
    niveauId: number;    
    niveauLib: string;
    niveauOrdre: number;
    niveauDescription: string;
}

interface TCivilite {
    civiliteId: number;
    civiliteLib: string;
}

interface TLigue {
    ligueId: number;
    ligueLib: string;
}

interface TClub {
    clubId: number;
    clubLib: string;
}

interface TEpreuve {
    epreuveId: number;
    epreuveLib: string;
}

interface TCategorie {
    categorieId: number;
    categorieLib: string;
}

interface TUserLigue {
    //userId: number;
    ligueId: number;
    ligueLib: string;
}

interface TUserCategorie {
    //userId: number;
    categorieId: number;
    categorieLib: string;
}

interface TUserEpreuve {
    //userId: number;
    epreuveId: number;
    epreuveLib: string;
}

interface TUserClub {
    //userId: number;
    clubId: number;
    clubLib: string;
}

interface TDisponibilite{
    id: number;
    jour: string;
    heureDebut: string;
    heureFin: string;
}

const Compte: React.FC = () => {
    
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState<string>('');

    const [user, setUser] = useState<User | null>(null);
    const [hand, setHand] = useState<THand[]>([]);
    const [civilite, setCivilite] = useState<TCivilite[]>([]);
    const [departement, setDepartement] = useState<TDepartement[]>([]);
    const [niveau, setNiveau] = useState<TNiveau[]>([]);    
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [subscriptionStatus, setSubscriptionStatus] = useState<{
        status: string;
        subscriptionValidUntil: string | null;
      }>({ status: '', subscriptionValidUntil: null });
    

    const validateForm = (data: typeof formData) => {
        const errors: Record<string, string> = {};        

        // Required fields
        if (!data.nom.trim()) errors.nom = 'Le nom est requis';
        if (!data.prenom.trim()) errors.prenom = 'Le prénom est requis';
        if (!data.email.trim()) errors.email = 'L\'email est requis';
        if (!data.handId) errors.handId = 'Le placement préférentiel est requis';
        if (data.civiliteId === null || data.civiliteId === undefined) errors.civiliteId = 'La civilité est requise';
        if (data.classement === null || data.classement === undefined) errors.classement = 'Le classement est requis';
    
        // Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (data.email && !emailRegex.test(data.email)) {
            errors.email = 'Email invalide';
        }
    
        // Classement validation
        if (isNaN(Number(data.classement))) {
            errors.classement = 'Le classement doit être un nombre';
        }
    
        return errors;
    };

    const [formData, setFormData] = useState({
        userId: user?.userId || 0,
        nom: '',
        prenom: '',
        email: '',
        handId: 0,
        civiliteId: 0,
        classement: 0,
        departementId: 0,
        niveauId: 0,
        canBeSearched: false,
        dispoTournoi: false,
        dispoLibre: false
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };
    
    useEffect(() => {
        if (user) {
            setFormData({
                userId: user.userId,
                nom: user.nom,
                prenom: user.prenom,
                email: user.email,
                handId: user.handId,
                civiliteId: user.civiliteId,
                classement: user.classement,
                departementId: user.departementId,
                niveauId: user.niveauId,
                canBeSearched: user.canBeSearched,
                dispoTournoi: user.dispoTournoi,
                dispoLibre: user.dispoLibre
            });
        }
    }, [user]);


    useEffect(() => {

        const fetchUserData = async () => {
            try {
                const token = sessionStorage.getItem('accessToken');

                if (!token) {
                    setError('Token non trouvé');
                    setIsLoading(false);
                    return;
                }

                const response = await api.get('/users/me');
                // Take first item from array
                const userData = response.data;
                
                if (userData) {
                    setUser(userData);
                } else {
                    setError('Aucune donnée utilisateur trouvée');
                }
                

            } catch (error) {
                console.error('Erreur lors du chargement des données utilisateur:', error);
                setError('Erreur lors du chargement des données');
            } finally {
                setIsLoading(false);
            }
        };

        fetchUserData();
    }, []);
   
    useEffect(() => {
        const fetchHandData = async () => {
            try {
                const [handRes, civiliteRes, departementRes, niveauRes] = await Promise.all([
                    api.get('/hand'),
                    api.get('/civilite'),
                    api.get('/departement'),
                    api.get('/niveau')                    
                ]);
                
                setHand(handRes.data);
                setCivilite(civiliteRes.data);
                setDepartement(departementRes.data);
                setNiveau(niveauRes.data);                

            } catch (error) {
                console.error('Erreur lors du chargement des données genre ou civilite:', error);
            }
        };

        fetchHandData();
    }, []);

    useEffect(() => {

        const fetchSubscriptionStatus = async () => {
          
          const response = await api.get('/account/subscription-status');
                    
          const data = response.data;

          setSubscriptionStatus(data);
        };
    
        fetchSubscriptionStatus();
      }, []);
    

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const validationErrors = validateForm(formData);

        if (Object.keys(validationErrors).length > 0) {
            setErrorMessage(Object.values(validationErrors).join(' '));
            
            setTimeout(() => {
                setErrorMessage('');
            }, 4000);

            return;
        }
        try {
            await api.put('/users/update', formData);

            setSuccessMessage('Vos informations ont été modifiées avec succès !');
        
            // Auto-hide success message after 3 seconds
            setTimeout(() => {
                setSuccessMessage('');
            }, 3000);

        } catch (error: any) {
            setErrorMessage(error.response?.data?.message || 'Une erreur est survenue');
        }
    };
        // Update handleChange
    const handleChkChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target as HTMLInputElement;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
        }));
    };

    /*Gestion des ligues favorites*/

    const [favoriteLigues, setFavoriteLigues] = useState<TUserLigue[]>([]);
    const [availableLigues, setAvailableLigues] = useState<TLigue[]>([]);
    const [selectedLigueId, setSelectedLigueId] = useState<number | null>(null);
    
    useEffect(() => {
        const fetchAvailableLigues = async () => {
            try {
                const response = await api.get('/ligue');
                
                setAvailableLigues(response.data.filter((ligue: { ligueId: number; }) => !favoriteLigues.some(fav => fav.ligueId === ligue.ligueId)));
                
            } catch (error) {
                console.error('Error fetching available ligues:', error);
            }
        };
        
        fetchAvailableLigues();
    }, [favoriteLigues]);

    useEffect(() => {

        const fetchFavoriteLigues = async () => {
            try {
                const response = await api.get('/userligue');
                const favoriteLiguesData = response.data;                

                // Fetch ligue details for favorite ligues
                const ligueDetails = await Promise.all(
                    favoriteLiguesData.map(async (fav: { ligue: any; }) => {                        
                        const ligueResponse = await api.get(`/ligue/${fav.ligue.ligueId}`);                                               
                        return ligueResponse.data;
                    })
                );

                setFavoriteLigues(ligueDetails);
            } catch (error) {
                console.error('Error fetching favorite ligues:', error);
            }
        };
        
        fetchFavoriteLigues();
            
    }, []);

    const handleAddFavoriteLigue = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedLigueId) return;
                          
            // Find the ligue before API call
            const ligueToAdd = availableLigues.find(ligue => ligue.ligueId === selectedLigueId);
            if (!ligueToAdd) return;

            // Optimistic update
            setFavoriteLigues(prev => [...prev, ligueToAdd]);
            setAvailableLigues(prev => prev.filter(ligue => ligue.ligueId !== selectedLigueId));
            setSelectedLigueId(null);
        
        try {
            const response =  await api.post(`/userligue/${selectedLigueId}`);
          
            // Mettre à jour l'état local
            //setFavoriteLigues([...favoriteLigues, response.data]);
                                  
        } catch (error) {
          console.error('Erreur lors de l\'ajout de la ligue:', error);
        }
      };

    const handleRemoveFavoriteLigue = async (ligueId: number) => {
        // Find the ligue before API call
        const ligueToRemove = favoriteLigues.find(ligue => ligue.ligueId === ligueId);
        if (!ligueToRemove) return;

        // Optimistic update
        setFavoriteLigues(prev => prev.filter(ligue => ligue.ligueId !== ligueId));
        setAvailableLigues(prev => [...prev, ligueToRemove]);

        try {
            // API call after UI update
            await api.delete(`/userligue/${ligueId}`);
        } catch (error) {
            console.error('Error removing favorite ligue:', error);
            // Rollback on error
            setFavoriteLigues(prev => [...prev, ligueToRemove]);
            setAvailableLigues(prev => prev.filter(ligue => ligue.ligueId !== ligueId));
        }
    };

    /*Fin gestion ligues favorites*/

    /*Gestion des clubs favoris */
    /*
    const [favoriteClubs, setFavoriteClubs] = useState<TUserClub[]>([]);
    const [availableClubs, setAvailableClubs] = useState<TClub[]>([]);
    const [selectedClubId, setSelectedClubId] = useState<number | null>(null);
    
    useEffect(() => {
        const fetchAvailableClubs = async () => {
            try {
                const response = await api.get('/club');
                
                setAvailableClubs(response.data.filter((club: { clubId: number; }) => !favoriteClubs.some(fav => fav.clubId === club.clubId)));
                
            } catch (error) {
                console.error('Error fetching available clubs:', error);
            }
        };
        
        fetchAvailableClubs();
    }, [favoriteClubs]);

    useEffect(() => {

        const fetchFavoriteClubs = async () => {
            try {
                const response = await api.get('/userclub');
                const favoriteClubsData = response.data;                

                // Fetch ligue details for favorite ligues
                const clubDetails = await Promise.all(
                    favoriteClubsData.map(async (fav: { club: any; }) => {                        
                        const response = await api.get(`/club/${fav.club.clubId}`);                        
                        return response.data;
                    })
                );

                setFavoriteClubs(clubDetails);
            } catch (error) {
                console.error('Error fetching favorite clubs:', error);
            }
        };
        
        fetchFavoriteClubs();
            
    }, []);

    const handleAddFavoriteClub = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedClubId) return;
                          
            // Find the ligue before API call
            const clubToAdd = availableClubs.find(club => club.clubId === selectedClubId);
            if (!clubToAdd) return;

            // Optimistic update
            setFavoriteClubs(prev => [...prev, clubToAdd]);
            setAvailableClubs(prev => prev.filter(club => club.clubId !== selectedClubId));
            setSelectedClubId(null);
        
        try {
            const response =  await api.post(`/userclub/${selectedClubId}`);                      
                                  
        } catch (error) {
          console.error('Erreur lors de l\'ajout du club:', error);
        }
      };

    const handleRemoveFavoriteClub = async (clubId: number) => {
        // Find the club before API call
        const clubToRemove = favoriteClubs.find(club => club.clubId === clubId);
        if (!clubToRemove) return;

        // Optimistic update
        setFavoriteClubs(prev => prev.filter(club => club.clubId !== clubId));
        setAvailableClubs(prev => [...prev, clubToRemove]);

        try {
            // API call after UI update
            await api.delete(`/userclub/${clubId}`);
        } catch (error) {
            console.error('Error removing favorite club:', error);
            // Rollback on error
            setFavoriteClubs(prev => [...prev, clubToRemove]);
            setAvailableClubs(prev => prev.filter(club => club.clubId !== clubId));
        }
    };*/
    
    /*fin gestion clubs favoris */

/*Gestion des epreuves favoris */
    
const [favoriteEpreuves, setFavoriteEpreuves] = useState<TUserEpreuve[]>([]);
const [availableEpreuves, setAvailableEpreuves] = useState<TEpreuve[]>([]);
const [selectedEpreuveId, setSelectedEpreuveId] = useState<number | null>(null);

useEffect(() => {
    const fetchAvailableEpreuves = async () => {
        try {
            const response = await api.get('/epreuve');
            
            setAvailableEpreuves(response.data.filter((epreuve: { epreuveId: number; }) => !favoriteEpreuves.some(fav => fav.epreuveId === epreuve.epreuveId)));            

        } catch (error) {
            console.error('Error fetching available epreuves:', error);
        }
    };
    
    fetchAvailableEpreuves();
}, [favoriteEpreuves]);

useEffect(() => {

    const fetchFavoriteEpreuves = async () => {
        try {
            const response = await api.get('/userepreuve');
            const favoriteEpreuvesData = response.data;                

            // Fetch epreuve details for favorite epreuves
            const epreuveDetails = await Promise.all(
                favoriteEpreuvesData.map(async (fav: { epreuve: any; }) => {                        
                    const response = await api.get(`/epreuve/${fav.epreuve.epreuveId}`);                    
                    return response.data;
                })
            );

            setFavoriteEpreuves(epreuveDetails);
        } catch (error) {
            console.error('Error fetching favorite epreuves:', error);
        }
    };
    
    fetchFavoriteEpreuves();
        
}, []);

const handleAddFavoriteEpreuve = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedEpreuveId) return;
                      
        // Find the epreuve before API call
        const epreuveToAdd = availableEpreuves.find(epreuve => epreuve.epreuveId === selectedEpreuveId);
        if (!epreuveToAdd) return;

        // Optimistic update
        setFavoriteEpreuves(prev => [...prev, epreuveToAdd]);
        setAvailableEpreuves(prev => prev.filter(epreuve => epreuve.epreuveId !== selectedEpreuveId));
        setSelectedEpreuveId(null);
    
    try {
        const response =  await api.post(`/userepreuve/${selectedEpreuveId}`);                      
                              
    } catch (error) {
      console.error('Erreur lors de l\'ajout du epreuve:', error);
    }
  };

const handleRemoveFavoriteEpreuve = async (epreuveId: number) => {
    // Find the epreuve before API call
    const epreuveToRemove = favoriteEpreuves.find(epreuve => epreuve.epreuveId === epreuveId);
    if (!epreuveToRemove) return;

    // Optimistic update
    setFavoriteEpreuves(prev => prev.filter(epreuve => epreuve.epreuveId !== epreuveId));
    setAvailableEpreuves(prev => [...prev, epreuveToRemove]);

    try {
        // API call after UI update
        await api.delete(`/userepreuve/${epreuveId}`);
    } catch (error) {
        console.error('Error removing favorite epreuve:', error);
        // Rollback on error
        setFavoriteEpreuves(prev => [...prev, epreuveToRemove]);
        setAvailableEpreuves(prev => prev.filter(epreuve => epreuve.epreuveId !== epreuveId));
    }
};

/*fin gestion epreuves favoris */

/*Gestion des categories favoris */
    
const [favoriteCategories, setFavoriteCategories] = useState<TUserCategorie[]>([]);
const [availableCategories, setAvailableCategories] = useState<TCategorie[]>([]);
const [selectedCategorieId, setSelectedCategorieId] = useState<number | null>(null);

useEffect(() => {
    const fetchAvailableCategories = async () => {
        try {
            const response = await api.get('/categorie');
            
            setAvailableCategories(response.data.filter((categorie: { categorieId: number; }) => !favoriteCategories.some(fav => fav.categorieId === categorie.categorieId)));
            
        } catch (error) {
            console.error('Error fetching available categories:', error);
        }
    };
    
    fetchAvailableCategories();
}, [favoriteCategories]);

useEffect(() => {

    const fetchFavoriteCategories = async () => {
        try {
            const response = await api.get('/usercategorie');
            const favoriteCategoriesData = response.data;                

            // Fetch categorie details for favorite categories
            const categorieDetails = await Promise.all(
                favoriteCategoriesData.map(async (fav: { categorie: any; }) => {                        
                    const response = await api.get(`/categorie/${fav.categorie.categorieId}`);                        
                    return response.data;
                })
            );

            setFavoriteCategories(categorieDetails);
        } catch (error) {
            console.error('Error fetching favorite categories:', error);
        }
    };
    
    fetchFavoriteCategories();
        
}, []);

const handleAddFavoriteCategorie = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCategorieId) return;
                      
        // Find the categorie before API call
        const categorieToAdd = availableCategories.find(categorie => categorie.categorieId === selectedCategorieId);
        if (!categorieToAdd) return;

        // Optimistic update
        setFavoriteCategories(prev => [...prev, categorieToAdd]);
        setAvailableCategories(prev => prev.filter(categorie => categorie.categorieId !== selectedCategorieId));
        setSelectedCategorieId(null);
    
    try {
        const response =  await api.post(`/usercategorie/${selectedCategorieId}`);                      
                              
    } catch (error) {
      console.error('Erreur lors de l\'ajout du categorie:', error);
    }
  };

const handleRemoveFavoriteCategorie = async (categorieId: number) => {
    // Find the categorie before API call
    const categorieToRemove = favoriteCategories.find(categorie => categorie.categorieId === categorieId);
    if (!categorieToRemove) return;

    // Optimistic update
    setFavoriteCategories(prev => prev.filter(categorie => categorie.categorieId !== categorieId));
    setAvailableCategories(prev => [...prev, categorieToRemove]);

    try {
        // API call after UI update
        await api.delete(`/usercategorie/${categorieId}`);
    } catch (error) {
        console.error('Error removing favorite categorie:', error);
        // Rollback on error
        setFavoriteCategories(prev => [...prev, categorieToRemove]);
        setAvailableCategories(prev => prev.filter(categorie => categorie.categorieId !== categorieId));
    }
};

/*fin gestion categories favoris */

    /* Gestion des disponibilités */    
    const [disponibilites, setDisponibilites] =  useState<TDisponibilite[]>([]);
    const [newDispo, setNewDispo] = useState({
    jour: '',
    heureDebut: '',
    heureFin: ''
    });

    // Fetch disponibilités
    useEffect(() => {
    const fetchDisponibilites = async () => {
        try {
        
            const response = await api.get('/disponibilite');
            setDisponibilites(response.data);

        } catch (error) {
            console.error('Error fetching disponibilités:', error);
        }
    };

    fetchDisponibilites();
    }, []);

    // Handle form change
    const handleDispoChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setNewDispo(prev => ({
        ...prev,
        [name]: value
    }));
    };

    // Handle form submit
    const handleDispoSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

  
        if (!newDispo.jour || !newDispo.heureDebut || !newDispo.heureFin) {
            return; // Validation basique
          }
          
          try {
            const dispoData = {
              jour: newDispo.jour,
              heureDebut: newDispo.heureDebut,
              heureFin: newDispo.heureFin,
              userId: user?.userId
            };
            
            const response = await api.post('/disponibilite', dispoData);
            
            // Mise à jour optimiste de l'état
            setDisponibilites(prev => [...prev, response.data]);
            
            // Réinitialisation du formulaire
            setNewDispo({ jour: '', heureDebut: '', heureFin: '' });
          } catch (error) {
            console.error('Error adding disponibilité:', error);
          }
        };

    // Handle remove disponibilité
    const handleRemoveDispo = async (id: number, e : React.MouseEvent) => {

        e.preventDefault(); // Empêche la soumission standard du formulaire
  
    try {
        await api.delete(`/disponibilite/${id}`);
        setDisponibilites(prev => prev.filter(dispo => dispo.id !== id));
    } catch (error) {
        console.error('Error removing disponibilité:', error);
    }
    };

    if (error) {
        return (
            <>
                <Menu />
                <div className="flex justify-center items-center h-screen">
                    <div className="text-red-500">{error}</div>
                </div>
                <Footer />
            </>
        );
    }

    if (isLoading) {
        return (
            <>
                <Menu />
                <div className="flex justify-center items-center h-screen">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
                </div>
                <Footer />
            </>
        );
    }

    if (!user) {
        return (
            <>
                <Menu />
                <div className="flex justify-center items-center h-screen">
                    <div className="text-gray-500">Aucune donnée utilisateur disponible</div>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Menu />
            <div className="min-h-screen">
                <div className="container mx-auto px-4">
                    {/* Profile Header */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden mb-6">
                        <div className="h-48">
                            <ImgCompte />
                        </div>
                        <div className="p-6">
                            <h1 className="text-2xl font-bold text-gray-800">
                                {user && `Bienvenue ${user.prenom} ${user.nom}`}
                            </h1>                            
                        </div>
                    </div>

                    {/* Profile Content */}
                    <form>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Informations personnelles */}
                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-semibold mb-4">Informations personnelles</h2>
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">
                                                Nom
                                            </p>
                                            <label className="input input-bordered flex items-center gap-2">                                                                                        
                                                <input name="nom" type="text" className="grow" value={formData.nom} onChange={handleChange}/>                                            
                                            </label>
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-700">
                                                Prénom
                                            </p>
                                            <label className="input input-bordered flex items-center gap-2">                                            
                                                <input name="prenom" required type="text" className="grow" value={formData.prenom} onChange={handleChange}/>
                                            </label>   
                                        </div>
                                    </div>
                                    <div>
                                        <label className="input input-bordered flex items-center gap-2">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 16 16"
                                                fill="currentColor"
                                                className="h-4 w-4 opacity-70">
                                                <path
                                                d="M2.5 3A1.5 1.5 0 0 0 1 4.5v.793c.026.009.051.02.076.032L7.674 8.51c.206.1.446.1.652 0l6.598-3.185A.755.755 0 0 1 15 5.293V4.5A1.5 1.5 0 0 0 13.5 3h-11Z" />
                                                <path
                                                d="M15 6.954 8.978 9.86a2.25 2.25 0 0 1-1.956 0L1 6.954V11.5A1.5 1.5 0 0 0 2.5 13h11a1.5 1.5 0 0 0 1.5-1.5V6.954Z" />
                                            </svg>
                                            <input name="email" type="email" required className="grow" value={formData.email} onChange={handleChange}/>
                                            </label>   
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                        <label className="block text-sm font-medium text-gray-700">Département</label>
                                            <select onChange={handleChange} required name="departementId" className="select select-bordered select-sm w-full max-w-xs">
                                                {departement.map((d) => (
                                                    <option key={d.departementId} value={d.departementId}
                                                        selected = {d.departementId === user.departementId}
                                                    >
                                                        {d.departementNum} - {d.departementLib}
                                                    </option>
                                                ))}
                                            </select>                                                                                
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Niveau</label>
                                            <select onChange={handleChange} required name="niveauId" className="select select-bordered select-sm w-full max-w-xs">
                                                {niveau.map((o) => (
                                                    <option key={o.niveauId} value={o.niveauId}
                                                        selected = {o.niveauId === user.niveauId}
                                                    >
                                                        {o.niveauOrdre} - {o.niveauLib}
                                                    </option>
                                                ))}
                                            </select>                                                       
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                        <label className="block text-sm font-medium text-gray-700">Placement Préférentiel</label>
                                            <select onChange={handleChange} required name="handId" className="select select-bordered select-sm w-full max-w-xs">
                                                {hand.map((h) => (
                                                    <option key={h.handId} value={h.handId}
                                                        selected = {h.handId === user.handId}
                                                    >
                                                        {h.handLib}
                                                    </option>
                                                ))}
                                            </select>                                                                                
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Genre</label>
                                            <select onChange={handleChange} required name="civiliteId" className="select select-bordered select-sm w-full max-w-xs">
                                                {civilite.map((c) => (
                                                    <option key={c.civiliteId} value={c.civiliteId}
                                                        selected = {c.civiliteId === user.civiliteId}
                                                    >
                                                        {c.civiliteLib}
                                                    </option>
                                                ))}
                                            </select>                                                       
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-sm font-medium text-gray-700">
                                            Classement
                                        </p>
                                        <label className="input input-bordered flex items-center gap-2">                                        
                                            <input name="classement" type="text" className="grow" value={formData.classement} onChange={handleChange}/>
                                        </label>                 
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 mt-4">
                                        <div className="flex flex-col gap-2">
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="canBeSearched"
                                                    checked={formData.canBeSearched}
                                                    onChange={handleChkChange}
                                                    className="checkbox checkbox-warning"
                                                />
                                                <span className="text-sm font-medium text-gray-700">Peut être recherché par d'autres joueurs ( dans la recherche des partenaires )</span>
                                            </label>
                                            
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="dispoTournoi"
                                                    checked={formData.dispoTournoi}
                                                    onChange={handleChkChange}
                                                    className="checkbox checkbox-warning"
                                                />
                                                <span className="text-sm font-medium text-gray-700">Disponible pour les tournois</span>
                                            </label>
                                            
                                            <label className="flex items-center space-x-2">
                                                <input
                                                    type="checkbox"
                                                    name="dispoLibre"
                                                    checked={formData.dispoLibre}
                                                    onChange={handleChkChange}
                                                    className="checkbox checkbox-warning"
                                                />
                                                <span className="text-sm font-medium text-gray-700">Disponible pour le jeu libre</span>
                                            </label>
                                        </div>
                                    </div>
                                    
                                </div>
                                    {errorMessage && (                                    
                                        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative my-4" role="alert duration-600">
                                            {errorMessage}
                                        </div>
                                    )}

                                    {/* Success Message */}
                                    {successMessage && (
                                        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative my-4 transition-opacity duration-600">
                                            {successMessage}
                                        </div>
                                    )}
                                <button 
                                    className="mt-6 w-full bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors duration-200 ease-in-out"
                                    onClick={handleSubmit} 
                                >
                                    Modifier mes informations
                                </button>
                            </div>     


                            <div className="bg-white rounded-lg shadow-md p-6">
                                <h2 className="text-xl font-semibold mb-4">Disponibilités pour le jeu libre</h2>
                                <form onSubmit={handleDispoSubmit}>
                                <div className="space-y-4">
                                    <div>
                                    <label className="block text-sm font-medium text-gray-700">Jour</label>
                                    <select
                                        name="jour"
                                        value={newDispo.jour}
                                        onChange={handleDispoChange}
                                        className="select select-bordered select-sm w-full"
                                    >
                                        <option value="">Sélectionner un jour</option>
                                        <option value="Lundi">Lundi</option>
                                        <option value="Mardi">Mardi</option>
                                        <option value="Mercredi">Mercredi</option>
                                        <option value="Jeudi">Jeudi</option>
                                        <option value="Vendredi">Vendredi</option>
                                        <option value="Samedi">Samedi</option>
                                        <option value="Dimanche">Dimanche</option>
                                    </select>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Heure de début</label>
                                        <input
                                        type="time"
                                        name="heureDebut"
                                        value={newDispo.heureDebut}
                                        onChange={handleDispoChange}
                                        className="input input-bordered w-full"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Heure de fin</label>
                                        <input
                                        type="time"
                                        name="heureFin"
                                        value={newDispo.heureFin}
                                        onChange={handleDispoChange}
                                        className="input input-bordered w-full"
                                        />
                                    </div>
                                    </div>
                                </div>
                                <button
                                     onClick={handleDispoSubmit}
                                    className="mt-4 w-full bg-orange-500 text-white py-2 px-4 rounded-md hover:bg-orange-600 transition-colors"
                                >
                                    Ajouter disponibilité
                                </button>
                                </form>

                                <div className="mt-6">
                                <h3 className="text-lg font-semibold mb-4">Vos disponibilités</h3>
                                <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
                                    {disponibilites.length === 0 ? (
                                    <p className="text-gray-500 text-center">Aucune disponibilité enregistrée</p>
                                    ) : (
                                    disponibilites.map(dispo => (
                                        <div key={dispo.id} className="flex items-center justify-between">
                                        <span>{dispo.jour} de {dispo.heureDebut.substring(0,5)} à {dispo.heureFin.substring(0,5)}</span>
                                        <button
                                            onClick={(e) => handleRemoveDispo(dispo.id, e)}
                                            className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                        >
                                            Retirer
                                        </button>
                                        </div>
                                    ))
                                    )}
                                </div>
                                </div>
                            </div>


                            <div className="bg-white rounded-lg shadow-md p-6">
                                <div>
                                    <h3 className="text-xl font-semibold mb-4 flex items-center">
                                        Vos préférences de notifications
                                    </h3>
                                    <p className="text-sm text-gray-600 mb-4">
                                        Ajoutez vos préférences de notifications pour les ligues, catégories et épreuves qui vous intéressent.
                                    </p>
                                </div>
                                <div>
                                <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className="h-6 w-6 text-green-500 mr-2"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0zm7.72 3.53a.75.75 0 001.06 0l5.25-5.25a.75.75 0 10-1.06-1.06l-4.72 4.72-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Vos ligues favorites
                                    </h3>
                                    <div className="flex items-center space-x-4">
                                        <select
                                            value={selectedLigueId || ''}
                                            onChange={(e) => setSelectedLigueId(Number(e.target.value))}
                                            className="p-2 border rounded-lg"
                                        >
                                            <option value="" disabled>Choisir une ligue</option>
                                            {availableLigues.map(ligue => (
                                                <option key={ligue.ligueId} value={ligue.ligueId}>
                                                    {ligue.ligueLib}
                                                </option>
                                            ))}
                                        </select>
                                        
                                    </div>
                                    
                                    <div className="mb-4">
                                        <button 
                                            onClick={handleAddFavoriteLigue}
                                            className="mt-2 btn btn-sm bg-orange-600 hover:bg-orange-400 text-white"                                            
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
                                        {favoriteLigues.map(ligue => (
                                            <div key={ligue.ligueId} className="flex items-center justify-between">
                                                <span>Ligue {ligue.ligueLib}</span>
                                                <button
                                                    onClick={() => handleRemoveFavoriteLigue(ligue.ligueId)}
                                                    className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                                >
                                                    Retirer
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                {/*<div className="mt-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className="h-6 w-6 text-green-500 mr-2"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0zm7.72 3.53a.75.75 0 001.06 0l5.25-5.25a.75.75 0 10-1.06-1.06l-4.72 4.72-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Vos clubs favoris
                                    </h3>
                                    <div className="flex items-center space-x-4">
                                        <select
                                            value={selectedClubId || ''}
                                            onChange={(e) => setSelectedClubId(Number(e.target.value))}
                                            className="p-2 border rounded-lg"
                                        >
                                            <option value="" disabled>Choisir un club</option>
                                            {availableClubs.map(club => (
                                                <option key={club.clubId} value={club.clubId}>
                                                    {club.clubLib}
                                                </option>
                                            ))}
                                        </select>
                                        
                                    </div>
                                    
                                    <div className="mb-4">
                                        <button 
                                            onClick={handleAddFavoriteClub}
                                            className="mt-2 btn btn-sm bg-orange-600 hover:bg-orange-400 text-white"                                            
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
                                        {favoriteClubs.map(club => (
                                            <div key={club.clubId} className="flex items-center justify-between">
                                                <span>{club.clubLib}</span>
                                                <button
                                                    onClick={() => handleRemoveFavoriteClub(club.clubId)}
                                                    className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                                >
                                                    Retirer
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>*/}

                                <div className="mt-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className="h-6 w-6 text-green-500 mr-2"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0zm7.72 3.53a.75.75 0 001.06 0l5.25-5.25a.75.75 0 10-1.06-1.06l-4.72 4.72-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Vos epreuves favorites
                                    </h3>
                                    <div className="flex items-center space-x-4">
                                        <select
                                            value={selectedEpreuveId || ''}
                                            onChange={(e) => setSelectedEpreuveId(Number(e.target.value))}
                                            className="p-2 border rounded-lg"
                                        >
                                            <option value="" disabled>Choisir une epreuve</option>
                                            {availableEpreuves.map(epreuve => (
                                                <option key={epreuve.epreuveId} value={epreuve.epreuveId}>
                                                    {epreuve.epreuveLib}
                                                </option>
                                            ))}
                                        </select>
                                        
                                    </div>
                                    
                                    <div className="mb-4">
                                        <button 
                                            onClick={handleAddFavoriteEpreuve}
                                            className="mt-2 btn btn-sm bg-orange-600 hover:bg-orange-400 text-white"                                            
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
                                        {favoriteEpreuves.map(epreuve => (
                                            <div key={epreuve.epreuveId} className="flex items-center justify-between">
                                                <span>{epreuve.epreuveLib}</span>
                                                <button
                                                    onClick={() => handleRemoveFavoriteEpreuve(epreuve.epreuveId)}
                                                    className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                                >
                                                    Retirer
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className="mt-6">
                                    <h3 className="text-lg font-semibold mb-4 flex items-center">
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            className="h-6 w-6 text-green-500 mr-2"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M2.25 12a9.75 9.75 0 1119.5 0 9.75 9.75 0 01-19.5 0zm7.72 3.53a.75.75 0 001.06 0l5.25-5.25a.75.75 0 10-1.06-1.06l-4.72 4.72-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                        Vos categories favorites
                                    </h3>
                                    <div className="flex items-center space-x-4">
                                        <select
                                            value={selectedCategorieId || ''}
                                            onChange={(e) => setSelectedCategorieId(Number(e.target.value))}
                                            className="p-2 border rounded-lg"
                                        >
                                            <option value="" disabled>Choisir un categorie</option>
                                            {availableCategories.map(categorie => (
                                                <option key={categorie.categorieId} value={categorie.categorieId}>
                                                    {categorie.categorieLib}
                                                </option>
                                            ))}
                                        </select>
                                        
                                    </div>
                                    
                                    <div className="mb-4">
                                        <button 
                                            onClick={handleAddFavoriteCategorie}
                                            className="mt-2 btn btn-sm bg-orange-600 hover:bg-orange-400 text-white"                                            
                                        >
                                            Ajouter
                                        </button>
                                    </div>

                                    <div className="space-y-4 bg-gray-100 p-4 rounded-lg">
                                        {favoriteCategories.map(categorie => (
                                            <div key={categorie.categorieId} className="flex items-center justify-between">
                                                <span>{categorie.categorieLib}</span>
                                                <button
                                                    onClick={() => handleRemoveFavoriteCategorie(categorie.categorieId)}
                                                    className="px-2 py-1 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors"
                                                >
                                                    Retirer
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>


                            </div>   
                                                                     
                        </div>
                    </form>
                                                            
                </div>
            </div>
            <Footer />
        </>
    );
};

export default Compte;